// src/examples/Sidenav/index.js
import { useMemo, useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { NavLink, useLocation } from "react-router-dom";
import {
  Drawer,
  Box,
  IconButton,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Tooltip,
  Avatar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { motion } from "framer-motion"; // 👈 Animaciones
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AppleIcon from "@mui/icons-material/Apple";
import AdminTrustLogo from "./AdminTrustLogo";
import { useMaterialUIController, setMiniSidenav } from "context";
import { obtenerUsuario, hasAllPerms, hasAnyPerm } from "../../features/auth/services/authServices";
import SucursalSwitcher from "../../features/sucursales/components/SucursalSwitcher";

const NAV_WIDTH = 224;
const NAV_MINI = 68;

const NAME_ALIASES = {
  "iphoneshop_1@gmail.com": "Agustin Riffel",
  "iphoneshop_2@gmail.com": "Rocio Riffel",
  "iphoneshop_3@gmail.com": "Alejo Implementer",
};

/* ====== Framer Motion variants ====== */
// Slide + fade del bloque completo del sidenav
const sideNavSlide = {
  hidden: { opacity: 0, x: -16 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.45, ease: "easeOut" }, // 👈 un poco más lento
  },
};

// “Caída” de cada item del menú
const listItemVariants = {
  hidden: { opacity: 0, y: -24 },
  visible: (index = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,                        // 👈 más lento
      ease: [0.22, 0.61, 0.36, 1],          // easeOut lindo
      delay: 0.09 * index,                  // 👈 stagger más marcado
    },
  }),
};

// CP5.4: un ítem con `perms` solo se muestra si el usuario los tiene (misma
// regla que las rutas: hasAllPerms). Sin usuario todavía, no se muestra.
// Las secciones que quedan sin ítems no se muestran.
export function buildSectionsFromRoutes(routes = [], user = null) {
  const out = [];
  let current = { title: null, items: [] };
  const cerrar = () => { if (current.items.length) out.push(current); };
  routes.forEach((r, idx) => {
    if (r.type === "title") {
      cerrar();
      current = { title: r.title || r.name || "", items: [] };
    } else if (r.type === "collapse") {
      if (Array.isArray(r.perms) && r.perms.length && !(user && (r.permsAlguno ? hasAnyPerm(user, r.perms) : hasAllPerms(user, r.perms)))) return;
      current.items.push({
        key: r.key || `item-${idx}`,
        label: r.name,
        to: r.route,
        icon: r.icon,
      });
    } else if (r.type === "divider") {
      cerrar();
      out.push({ divider: true, key: r.key || `divider-${idx}` });
      current = { title: null, items: [] };
    }
  });
  cerrar();
  return out;
}

export default function Sidenav({ routes }) {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav } = controller;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();

  const [user, setUser] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Botón flotante (desde DashboardLayout)
  useEffect(() => {
    window.__openSidenav = () => setMobileOpen(true);
    window.__closeSidenav = () => setMobileOpen(false);
    window.__toggleSidenav = () => setMobileOpen((v) => !v);
    return () => {
      delete window.__openSidenav;
      delete window.__closeSidenav;
      delete window.__toggleSidenav;
    };
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const u = await obtenerUsuario();
        if (alive) setUser(u);
      } catch {
        if (alive) setUser(null);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const sections = useMemo(() => buildSectionsFromRoutes(routes, user), [routes, user]);

  // Bug: "Pagos a Proveedores" (/proveedores/pagos) matchea por prefijo
  // contra "Proveedores" (/proveedores) y ambos quedaban resaltados a la
  // vez. Fix general (no un hack puntual para esta ruta): entre todas las
  // rutas del menú que matchean el pathname actual (exacto o por
  // segmento), sólo la MÁS ESPECÍFICA (el `to` más largo) queda activa.
  const activeTo = useMemo(() => {
    const allTo = sections.flatMap((s) => (s.items || []).map((it) => it.to)).filter(Boolean);
    const matches = allTo.filter(
      (to) => location.pathname === to || location.pathname.startsWith(`${to}/`)
    );
    if (!matches.length) return null;
    return matches.reduce((best, to) => (to.length > best.length ? to : best));
  }, [sections, location.pathname]);
  const isActive = useCallback((to) => to === activeTo, [activeTo]);

  // Escala desktop (QA Dashboard V2 a 1920x1080): ítems de 30px con texto
  // 12px e íconos 16px; categorías 10px más livianas que los ítems. Sin
  // transform/zoom: medidas reales. overflowY sigue como red de seguridad
  // para ventanas más bajas.
  const renderSectionList = (showHeaders, collapsed) => (
    <Box
      sx={{
        flex: 1,
        py: 0.5,
        overflowY: "auto",
        // Scrollbar fina (solo aparece en ventanas bajas): la nativa de 15px
        // dejaba sin lugar a "Cta. corriente proveedores".
        "&::-webkit-scrollbar": { width: 6 },
        "&::-webkit-scrollbar-thumb": { bgcolor: "divider", borderRadius: 3 },
      }}
    >
      {sections.map((section, sidx) => {
        if (section.divider) {
          return <Divider key={section.key || `div-${sidx}`} sx={{ my: 0.5 }} />;
        }

        // Ocultar “Salir / Logout / Cerrar sesión” en móvil
        const items = (section.items || []).filter((it) => {
          if (!isMobile) return true;
          const byName = /^(salir|logout|cerrar\s*sesión)$/i.test(it.label || "");
          const byRoute = /(logout|signout|exit)$/i.test(it.to || "");
          return !(byName || byRoute);
        });
        if (!items.length) return null;

        return (
          <List
            key={section.title || `sec-${sidx}`}
            dense
            sx={{ px: collapsed ? 0.5 : 1, py: 0.25, mb: 0.5 }}
            subheader={
              showHeaders && section.title ? (
                <ListSubheader
                  disableSticky
                  sx={{
                    lineHeight: "20px",
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: "text.secondary",
                    bgcolor: "transparent",
                    fontSize: 10,
                    fontWeight: 600,
                    px: 1,
                  }}
                >
                  {section.title}
                </ListSubheader>
              ) : null
            }
          >
            {items.map((item, idx) => {
              const active = isActive(item.to);

              const button = (
                <ListItemButton
                  key={item.key || item.label}
                  component={NavLink}
                  to={item.to}
                  onClick={() => isMobile && setMobileOpen(false)}
                  sx={{
                    my: 0.125,
                    px: collapsed ? 0.75 : 1,
                    borderRadius: 2,
                    minHeight: 30,
                    justifyContent: collapsed ? "center" : "flex-start",
                    ...(active && {
                      bgcolor: "warning.main",
                      color: "#fff",
                      boxShadow: "0 4px 10px rgba(255,165,0,0.35)",
                      "& .MuiListItemIcon-root": { color: "#fff" },
                      "&:hover": { bgcolor: "warning.dark" },
                    }),
                    ...(!active && {
                      color: "text.primary",
                      "&:hover": { bgcolor: "action.hover" },
                    }),
                  }}
                >
                  {item.icon && (
                    <ListItemIcon
                      sx={{
                        minWidth: collapsed ? 0 : 24,
                        mr: collapsed ? 0 : 0.75,
                        color: active ? "#fff" : "text.secondary",
                        display: "grid",
                        placeItems: "center",
                        // El tema fuerza `fontSize: inherit !important` en SvgIcon:
                        // el tamaño del ícono se define en este contenedor.
                        fontSize: 16,
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                  )}
                  {!collapsed && (
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        variant: "caption",
                        sx: { fontSize: 12, fontWeight: active ? 700 : 600, lineHeight: 1.2, whiteSpace: "nowrap" },
                      }}
                    />
                  )}
                </ListItemButton>
              );

              const content = collapsed ? (
                <Tooltip key={item.key || item.label} title={item.label} placement="right">
                  <Box>{button}</Box>
                </Tooltip>
              ) : (
                button
              );

              // Animación individual de cada ítem (cae desde arriba, más lento)
              return (
                <Box
                  key={item.key || item.label}
                  component={motion.div}
                  initial="hidden"
                  animate="visible"
                  variants={listItemVariants}
                  custom={idx}
                >
                  {content}
                </Box>
              );
            })}
          </List>
        );
      })}
    </Box>
  );

  const paperWidth = miniSidenav ? NAV_MINI : NAV_WIDTH;
  const displayName = NAME_ALIASES[user?.correo] || user?.nombre || user?.correo;

  // --- Móvil: Drawer temporary ---
  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          sx: (t) => ({
            width: NAV_WIDTH,
            borderRight: "1px solid",
            borderBottom: "1px solid",
            borderColor: "divider",
            backgroundColor: t.palette.background.sidenav,
            color: t.palette.text.primary,
            display: "flex",
            flexDirection: "column",
            p: 0,
          }),
        }}
      >
        <Box sx={{ display: "grid", placeItems: "center", px: 1, py: 1 }}>
          <AdminTrustLogo size="nav" />
        </Box>

        {renderSectionList(true, false)}

        {/* User card (móvil) */}
        <Box
          sx={{
            borderTop: "1px solid",
            borderColor: "divider",
            px: 1.25,
            py: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 0.5,
              py: 0.4,
            }}
          >
            {/* Avatar + dot (Apple) */}
            <Box sx={{ position: "relative" }}>
              <Avatar
                sx={(t) => ({
                  width: 30,
                  height: 30,
                  bgcolor: "transparent",
                  color: t.palette.text.primary,
                  border: "1px solid",
                  borderColor: "divider",
                })}
              >
                <AppleIcon sx={{ fontSize: 16 }} />
              </Avatar>
              <Box
                sx={{
                  position: "absolute",
                  right: -1,
                  bottom: -1,
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  bgcolor: "success.main",
                  border: "2px solid",
                  borderColor: "background.sidenav",
                }}
              />
            </Box>

            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
              <Typography variant="caption" noWrap sx={{ fontWeight: 700, fontSize: 12.5, display: "block", lineHeight: 1.3 }}>
                {displayName || "—"}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography variant="caption" sx={{ color: "text.secondary", fontSize: 11 }}>
                  Online
                </Typography>
                <Typography variant="caption" sx={{ color: "text.disabled", fontSize: 11 }}>
                  ·
                </Typography>
                <SucursalSwitcher />
              </Box>
            </Box>
          </Box>
        </Box>
      </Drawer>
    );
  }

  // --- Desktop: Drawer permanent ---
  return (
    <Drawer
      variant="permanent"
      PaperProps={{
        sx: (t) => ({
          width: paperWidth,
          borderRight: "1px solid",
          borderBottom: "1px solid",
          borderColor: "divider",
          backgroundColor: t.palette.background.sidenav,
          color: t.palette.text.primary,
          display: "flex",
          flexDirection: "column",
          p: 0,
        }),
      }}
    >
      {/* Contenedor animado del sidenav */}
      <Box
        component={motion.div}
        initial="hidden"
        animate="visible"
        variants={sideNavSlide}
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            flexDirection: miniSidenav ? "column" : "row",
            alignItems: "center",
            justifyContent: miniSidenav ? "center" : "flex-start",
            gap: miniSidenav ? 0.5 : 1,
            px: miniSidenav ? 0.5 : 1.25,
            py: miniSidenav ? 0.5 : 0.75,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          {miniSidenav ? (
            <Box sx={{ display: "grid", placeItems: "center", width: "100%", rowGap: 0.25 }}>
              <AdminTrustLogo size="md" iconOnly />
              <IconButton
                size="small"
                onClick={() => setMiniSidenav(dispatch, false)}
                sx={{ color: "text.secondary", "&:hover": { bgcolor: "action.hover" } }}
                aria-label="Expandir menú"
              >
                <ChevronRightIcon />
              </IconButton>
            </Box>
          ) : (
            <>
              <Box
                component={NavLink}
                to="/dashboard"
                sx={{ textDecoration: "none", color: "inherit", display: "flex" }}
              >
                <AdminTrustLogo size="nav" />
              </Box>
              <Box sx={{ ml: "auto" }}>
                <IconButton
                  size="small"
                  onClick={() => setMiniSidenav(dispatch, true)}
                  sx={{ color: "text.secondary", fontSize: 16, "&:hover": { bgcolor: "action.hover" } }}
                  aria-label="Colapsar menú"
                >
                  <ChevronLeftIcon />
                </IconButton>
              </Box>
            </>
          )}
        </Box>

        {renderSectionList(!miniSidenav, miniSidenav)}

        {/* User card (desktop) */}
        {!miniSidenav && (
          <Box sx={{ borderTop: "1px solid", borderColor: "divider", px: 1, py: 0.5 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.875,
                px: 0.5,
                py: 0.5,
              }}
            >
              {/* Avatar + dot (Apple) */}
              <Box sx={{ position: "relative" }}>
                <Avatar
                  sx={(t) => ({
                    width: 28,
                    height: 28,
                    bgcolor: "transparent",
                    color: t.palette.text.primary,
                    border: "1px solid",
                    borderColor: "divider",
                  })}
                >
                  <AppleIcon sx={{ fontSize: 15 }} />
                </Avatar>
                <Box
                  sx={{
                    position: "absolute",
                    right: -1,
                    bottom: -1,
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: "success.main",
                    border: "2px solid",
                    borderColor: "background.sidenav",
                  }}
                />
              </Box>

              <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                <Typography variant="body2" noWrap sx={{ fontWeight: 700, fontSize: 12.5, lineHeight: 1.3 }}>
                  {displayName || "—"}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography variant="caption" sx={{ color: "text.secondary", fontSize: 10.5 }}>
                    Online
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.disabled", fontSize: 10.5 }}>
                    ·
                  </Typography>
                  <SucursalSwitcher />
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Drawer>
  );
}

Sidenav.propTypes = {
  routes: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.oneOf(["title", "collapse", "divider"]).isRequired,
      title: PropTypes.string,
      name: PropTypes.string,
      key: PropTypes.string,
      route: PropTypes.string,
      icon: PropTypes.node,
    })
  ),
};
Sidenav.defaultProps = { routes: [] };
